import type { GameEngine } from "../core/GameEngine";
import { GameBridge, type PlayerMotion } from "./GameBridge";
import { defineTools, type McpToolDef } from "./tools";

/** WebMCP browser API — Chrome 149+ Origin Trial, stable ~Q4 2026. */
interface ModelContext {
  registerTool(
    tool: {
      name: string;
      title?: string;
      description: string;
      inputSchema: Record<string, unknown>;
      execute: (args: Record<string, unknown>) => Promise<unknown> | unknown;
      annotations?: {
        readOnlyHint?: boolean;
        destructiveHint?: boolean;
        idempotentHint?: boolean;
        openWorldHint?: boolean;
      } | undefined;
    },
    options?: { signal?: AbortSignal },
  ): Promise<void>;
}

declare global {
  interface Document {
    modelContext?: ModelContext;
  }
}

/**
 * Registers WebMCP tools so browser-integrated AI agents can observe and control the game.
 * No-op when `document.modelContext` is not available.
 *
 * Requirements:
 * - Chrome 149+ with "WebMCP for testing" flag enabled (chrome://flags/#enable-webmcp-testing)
 * - Or join the Chrome Early Preview Program
 */
export function initMcp(engine: GameEngine, motion: PlayerMotion): AbortController | undefined {
  const mc = document.modelContext;
  if (!mc) {
    console.log(
      "[mcp] document.modelContext not available.",
      "\n  → chrome://flags → 'WebMCP for testing' → Enabled → Relaunch",
    );
    return undefined;
  }

  const bridge = new GameBridge(engine.context, motion);
  const tools = defineTools(bridge);
  const controller = new AbortController();
  let registered = 0;

  for (const tool of tools) {
    registerOne(mc, tool, controller.signal)
      .then(() => {
        registered++;
        if (registered === tools.length) {
          console.log(`[mcp] ${registered} tools registered — agents can now control the game`);
        }
      })
      .catch((err: unknown) => {
        console.warn(`[mcp] failed to register "${tool.name}":`, err);
      });
  }

  return controller;
}

async function registerOne(mc: ModelContext, tool: McpToolDef, signal: AbortSignal): Promise<void> {
  await mc.registerTool(
    {
      name: tool.name,
      title: tool.title,
      description: tool.description,
      inputSchema: tool.inputSchema,
      execute: (args) => tool.execute(args),
      annotations: tool.annotations,
    },
    { signal },
  );
}
